import React, { useCallback, useEffect, useRef } from 'react';
import { NavigationType, useLocation, useNavigationType } from 'react-router-dom';
import { EventedModal, ModalNames } from '|/app/modals';
import {
  LeaveGuard,
  RegisterLeaveGuard,
  VerifxLeaveGuardContext
} from '|/app/purchase/flow/mfe-routes/verifx-leave-guard-context';
import { StepElement } from '|/app/purchase/flow/steps-router';
import { Step } from '|/app/services';
import { NextStepNavigationContextProvider, PageEventContextProvider } from '|/context';
import { searchHasSentinel } from '|/context/navigation-sentinel';
import { AppFrameworkContextProvider } from '|/helpers/app-framework';
import styles from './verification-task-sheet.module.css';
import { getPageEventContext } from '../constants/page-event-mappings';

interface VerificationTaskSheetProps {
  step: Step;
  onSubmit: () => void;
  onClose: () => void;
}

const VerificationTaskSheet: React.FC<VerificationTaskSheetProps> = ({
  step,
  onSubmit,
  onClose
}) => {
  const leaveGuardRef = useRef<LeaveGuard | null>(null);
  const closedRef = useRef(false);

  const registerLeaveGuard = useCallback<RegisterLeaveGuard>(guard => {
    leaveGuardRef.current = guard;
    return () => {
      leaveGuardRef.current = null;
    };
  }, []);

  const closeOnce = useCallback(() => {
    if (closedRef.current) return;
    closedRef.current = true;
    onClose();
  }, [onClose]);

  // ds-modal 6.4.0 onBeforeClose: returning false keeps the sheet open. Only the "x" reaches it.
  const confirmClose = () => !leaveGuardRef.current?.requestLeave(closeOnce);

  // Browser Back. While the MFE is blocking, the router's blocker owns it: it holds the POP and the
  // MFE's warning decides. Closing here would unmount the MFE before that warning could show.
  const requestClose = () => {
    if (!leaveGuardRef.current?.isBlocking()) closeOnce();
  };

  // The back-close coordinator's sentinel is single-use and cannot be re-armed while the blocker holds
  // every navigation, so the sheet also closes when the router pops off the sentinel entry.
  const location = useLocation();
  const navigationType = useNavigationType();
  const sawSentinelRef = useRef(false);
  useEffect(() => {
    if (searchHasSentinel(location.search)) {
      sawSentinelRef.current = true;
      return;
    }
    if (sawSentinelRef.current && navigationType === NavigationType.Pop) closeOnce();
  }, [location, navigationType, closeOnce]);

  // Mounted only while a task is open (verifications-panel gates on openTaskStepId), so isOpen is
  // constant. Goes through EventedModal rather than the raw DS hook so the sheet joins the managed
  // back-close set — it wraps the verifx MFE, whose own modals stack on top of it.
  return (
    <EventedModal
      isOpen
      analyticsId={ModalNames.VerificationTaskSheet}
      modalHeader={step.name}
      onClose={requestClose}
      onBeforeClose={confirmClose}
      disableOutsideClose
      disableEscapeClose
      contentPadding={false}
      zIndex={700}
      classes={{ body: styles.modalBody }}
    >
      <PageEventContextProvider value={getPageEventContext(step.id)}>
        <NextStepNavigationContextProvider
          value={{ handleNextStep: onSubmit, ensureInProgress: () => Promise.resolve() }}
        >
          <AppFrameworkContextProvider>
            <VerifxLeaveGuardContext.Provider value={registerLeaveGuard}>
              <StepElement
                step={step}
                isHidden={false}
                GetDashboardAndRouteToNextStep={() => {
                  onSubmit();
                }}
              />
            </VerifxLeaveGuardContext.Provider>
          </AppFrameworkContextProvider>
        </NextStepNavigationContextProvider>
      </PageEventContextProvider>
    </EventedModal>
  );
};

export default VerificationTaskSheet;
